import React from 'react';
import { TrendingUp, Plus, Edit, Trash2, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const periodLabels = {
  monthly: 'Bulanan',
  quarterly: 'Triwulan',
  yearly: 'Tahunan',
};

function KPICard({ kpi, index, getDepartmentName, onEdit, onDelete }) {
  // Format angka tanpa desimal yang tidak perlu
  const formatNumber = value => {
    if (value === null || value === undefined || value === '') return '0';
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return '0';
    // Jika angka bulat, tampilkan tanpa desimal
    if (Number.isInteger(num)) {
      return num.toString();
    }
    // Jika ada desimal, hapus trailing zeros
    return num.toString().replace(/\.?0+$/, '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <div className="bg-white rounded-lg border shadow-sm hover:shadow-lg transition-shadow h-full p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div className="flex gap-1">
            <button onClick={() => onEdit(kpi)} className="p-2 hover:bg-gray-100 rounded-lg">
              <Edit className="w-4 h-4 text-gray-600" />
            </button>
            <button onClick={() => onDelete(kpi.id)} className="p-2 hover:bg-gray-100 rounded-lg">
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
        <h3 className="text-lg font-semibold mb-2">{kpi.name}</h3>
        <p className="text-sm text-gray-500 mb-4 line-clamp-2">
          {kpi.description || 'Tidak ada deskripsi'}
        </p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Target</span>
            <span className="font-semibold">
              {formatNumber(kpi.target_value)} {kpi.unit}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Bobot</span>
            <span className="font-semibold">{formatNumber(kpi.weight)}%</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Periode</span>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full border">
              {periodLabels[kpi.period] || kpi.period}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Departemen</span>
            <span className="text-xs text-gray-600">{getDepartmentName(kpi.department_id)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="bg-white rounded-lg border shadow-sm py-12 text-center">
      <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <p className="text-gray-500">Belum ada KPI</p>
      <button
        onClick={onAdd}
        className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md font-medium inline-flex items-center"
      >
        <Plus className="w-4 h-4 mr-2" /> Tambah KPI Pertama
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
    </div>
  );
}

export default function KPICards({ kpis, loading, getDepartmentName, onEdit, onDelete, onAdd }) {
  if (loading) return <LoadingState />;
  if (kpis.length === 0) return <EmptyState onAdd={onAdd} />;

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {kpis.map((kpi, index) => (
        <KPICard
          key={kpi.id}
          kpi={kpi}
          index={index}
          getDepartmentName={getDepartmentName}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
